/**
 * WHAT THE TABLE ACTUALLY RULED.
 *
 * One `resolveX` per settings row, each a pure function of the stored
 * document. They exist because a stored `undefined` is not the same as a
 * stored `false`: almost every rule here has a non-false default, and reading
 * `permissions.houseRules.x` directly gets a fresh session wrong. Nothing may
 * read a house rule except through the resolver that owns its default.
 *
 * These are PURE - no Firestore, no React, no I/O. They lived in
 * `multiplayer.ts` until v8.90.0, which is why six components imported the
 * multiplayer module to render a checkbox.
 *
 * Several memoise on their own answer rather than rebuilding an object per
 * call: they are read during render and land in dependency arrays, so a fresh
 * object each time would defeat every memo downstream.
 */
import { settingByKey, tableDefaultsFrom, viewerChoice } from './registry';
import { DEFAULT_ASSIST_BONUS, isAssistBonus } from '../narrative/assist';
import { DEFAULT_SIGNATURE_REACH_KEY, isSignatureReachKey, type SignatureReachKey } from '../stealth/noise';
import type { AssistBonusKey } from '../narrative/assist';
import { DEFAULT_UNNATURAL_DOS_MODE, isUnnaturalDosMode } from '../unnaturalDos';
import { DEFAULT_STACK_POLICY, isStackPolicy, type StackPolicy } from '../stackPolicy';
import type { UnnaturalDosMode } from '../unnaturalDos';
import { isCoverConflictMode } from '../coverConflict';
import { DEFAULT_COVER_NEAR_TARGET, isCoverNearTarget } from '../battlefield/coverNearTarget';
import type { CoverNearTarget } from '../battlefield/coverNearTarget';
import type { CoverConflictMode } from '../coverConflict';
import {
  DEFAULT_INITIATIVE_ARRIVAL,
  DEFAULT_INITIATIVE_TIE_BREAK,
  isInitiativeArrival,
  isInitiativeTieBreak,
} from '../initiative';
import type { InitiativeArrival, InitiativeTieBreak } from '../initiative';
import { ARC_PRESETS } from '../battlefield/facing';
import type { ArcPreset } from '../battlefield/facing';
import { isReactionAlarmPolicy, DEFAULT_REACTION_ALARM } from '../reactionAlarm';
import type { ReactionAlarmPolicy } from '../reactionAlarm';
import { isBlastSelfPolicy, DEFAULT_BLAST_SELF } from '../blastRules';
import type { BlastSelfPolicy } from '../blastRules';
import { isAveragedDamageMode, DEFAULT_AVERAGED_DAMAGE } from '../survival/averagedDamage';
import { isChallengeFailure, DEFAULT_CHALLENGE_FAILURE, type ChallengeFailure } from '../killTeam/houseRules';
import type { AveragedDamageMode } from '../survival/averagedDamage';
import type { PermissionsDoc } from './permissions';

/**
 * The condition-modifier house rule is ON by default: it applies unless a GM has
 * explicitly stored `false`. `undefined` (fresh session / never toggled) ⇒ true.
 */
export function resolveApplyConditionModifiers(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.applyConditionModifiers !== false;
}

/** The advisory action-economy readout (v8.309.0). Default ON. */
export function resolveShowActionEconomy(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.showActionEconomy !== false;
}

/**
 * Does Immobilised impose −30 WS and prevent Dodge?
 *
 * Defaults to TRUE: it is the rule as written, and it is what a vehicle
 * has answered to since v6.39.0. Unset ⇒ on, explicit false ⇒ off.
 */
export function resolveImmobilisedAffectsCombat(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.immobilisedAffectsCombat !== false;
}

/** How far each Signature step carries (plan armoury): 'standard' (10 / 50 / 150 m) unless the table says otherwise. */
export function resolveSignatureReach(permissions: PermissionsDoc | null | undefined): SignatureReachKey {
  const stored = permissions?.houseRules?.signatureReach;
  return isSignatureReachKey(stored) ? stored : DEFAULT_SIGNATURE_REACH_KEY;
}

/** What each helper adds to a test, as the setting's key: '10' as written unless the table says otherwise. */
export function resolveAssistBonus(permissions: PermissionsDoc | null | undefined): AssistBonusKey {
  const stored = permissions?.houseRules?.assistBonus;
  return isAssistBonus(stored) ? stored : DEFAULT_ASSIST_BONUS;
}

/**
 * Do the helpless rules (cannot miss, damage rolled twice) reach ranged
 * attacks? Defaults to FALSE: as written they are for melee.
 */
export function resolveHelplessRanged(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.helplessRanged === true;
}

/**
 * Does a condition that forbids a reaction also disable the DELIBERATE
 * test on the sheet?
 *
 * Default TRUE. A reaction and a sheet test are not the same act - which
 * is why an untrained combatant may still roll a Dodge test and merely
 * carries a tag - but "cannot Dodge" forbids the action itself, and
 * offering the button invites a roll that cannot mean anything. Off
 * restores exactly the pre-v7.51.0 behaviour, for a GM who wants to call
 * for the roll anyway.
 */
export function resolveConditionsBlockSheetTests(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.conditionsBlockSheetTests !== false;
}

/**
 * The three deployment avoidance switches, as one object.
 *
 * Independent by request: structures and impassable ground default ON,
 * crowding defaults OFF - some tables genuinely stack models in a
 * doorway or a transport, and the overlap warning still appears either
 * way.
 *
 * MEMOISED ON THE THREE ANSWERS, not rebuilt per call: this is read
 * during render and lands in dependency arrays, and a fresh object each
 * time would defeat every memo downstream (the same rule
 * `resolveConditionModOptions` follows).
 */
const AVOID_CACHE = new Map<string, { avoidStructures: boolean; avoidImpassable: boolean; avoidCombatants: boolean }>();

export function resolveDeployAvoidance(permissions: PermissionsDoc | null | undefined): {
  avoidStructures: boolean;
  avoidImpassable: boolean;
  avoidCombatants: boolean;
} {
  const rules = permissions?.houseRules;
  const avoidStructures = rules?.deployAvoidStructures !== false;
  const avoidImpassable = rules?.deployAvoidImpassable !== false;
  const avoidCombatants = rules?.deployAvoidCombatants === true;
  const key = `${avoidStructures}|${avoidImpassable}|${avoidCombatants}`;
  let hit = AVOID_CACHE.get(key);
  if (!hit) {
    hit = Object.freeze({ avoidStructures, avoidImpassable, avoidCombatants });
    AVOID_CACHE.set(key, hit);
  }
  return hit;
}

/**
 * Does the ground modify whoever stands on it?
 *
 * Default TRUE - a surface a GM has given effects to is meant to have
 * them. Off leaves painted ground as colour and commentary, for a table
 * that wants the map descriptive and the rules in their heads.
 */
export function resolveSurfacesModifyCombatants(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.surfacesModifyCombatants !== false;
}

/** Does loading a map ring everyone at the marker again? */
export function resolveRedeployOnMapLoad(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.redeployOnMapLoad === true;
}

/**
 * Every per-condition switch, as one object for `conditionModifiers.ts`.
 *
 * TWO FROZEN SINGLETONS rather than a fresh literal per call, because this
 * is read during render and sits in `useMemo` dependency arrays - a new
 * object each time would defeat every memo it reaches.
 */
/** One frozen record per combination of answers, built once: identity matters downstream. */
const COND_MODS = [true, false].flatMap((immobilisedAffectsCombat) =>
  [true, false].map((helplessRanged) => Object.freeze({ immobilisedAffectsCombat, helplessRanged })),
);

/**
 * The options every reaction reading takes (the defence prompt, the rails): the condition house rule and which of
 * its conditions the table switched off. One place, so the readings cannot drift.
 */
export function resolveReactionOptions(permissions: PermissionsDoc | null | undefined): {
  applyConditionMods: boolean;
  condOptions: ReturnType<typeof resolveConditionModOptions>;
} {
  return {
    applyConditionMods: resolveApplyConditionModifiers(permissions),
    condOptions: resolveConditionModOptions(permissions),
  };
}

export function resolveConditionModOptions(permissions: PermissionsDoc | null | undefined): {
  immobilisedAffectsCombat: boolean;
  helplessRanged: boolean;
} {
  const immobilised = resolveImmobilisedAffectsCombat(permissions);
  const ranged = resolveHelplessRanged(permissions);
  return COND_MODS.find((o) => o.immobilisedAffectsCombat === immobilised && o.helplessRanged === ranged)!;
}

/**
 * Does Fatigue slow you down on the map?
 *
 * Defaults to FALSE: it is a house rule rather than a reading of the
 * book, and a table that has not asked for it should not find its
 * wounded crawling.
 */
export function resolveFatigueSlowsMovement(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.fatigueSlowsMovement === true;
}

/**
 * Roll start-of-turn effect tests automatically?
 *
 * Defaults to FALSE - unlike the condition-modifier rule. Prompting is
 * the default so the ally's Half Action assist stays available, and so
 * an automatic d100 per burning combatant per turn is opt-in rather
 * than something that quietly consumes dice every round.
 */
export function resolveAutoResolveTurnEffects(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.autoResolveTurnEffects === true;
}

/**
 * Do timed conditions count down each round?
 *
 * Defaults to FALSE, matching the old per-combatant default in
 * engine/factories.ts. Before v7.9.0 this lived on each combatant, which
 * meant the turn tracker had to force-write the flag onto every tab when
 * it was toggled - N Firestore writes, N rev bumps, and nothing at all
 * for a client that joined afterwards. One session-wide value has none of
 * those problems.
 *
 * It is deliberately NOT folded into projectDefender: the projection
 * round-trips through stateFromTarget on every updateTab, so a session
 * value injected there would be persisted back into each combatant's
 * CombatState - re-creating the per-combatant field this replaces.
 */
/**
 * Does going up cost movement?
 *
 * DEFAULT ON. Every rise within a stride used to be free, so a staircase
 * cost what the same distance of flat floor did - and the map has always
 * known the height it was carrying people over.
 */
export function resolveClimbCostsMovement(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.climbCostsMovement !== false;
}

/**
 * How near the TARGET a solid must stand for its cover to count (2026-10-25).
 *
 * DEFAULT '2' m: the user chose it knowing every existing map's cover changes. 'off' restores the old
 * rule (anywhere on the line). Validated rather than trusted, like every enum ruling.
 */
export function resolveCoverNearTarget(permissions: PermissionsDoc | null | undefined): CoverNearTarget {
  const stored = permissions?.houseRules?.coverNearTarget;
  return isCoverNearTarget(stored) ? stored : DEFAULT_COVER_NEAR_TARGET;
}

/**
 * How a cover set by hand and the map's own answer meet.
 *
 * DEFAULT 'highest' - the rule the map already applies between several
 * walls, extended to include the pin. Before v8.13.0 any pinned value
 * deleted the map's answer outright, which is 'pinned' here and is kept
 * so that no table's numbers move without them choosing it.
 *
 * Validated rather than trusted: a later build may write a ruling this
 * one has never heard of, and the document outlives the code.
 */
export function resolveCoverConflictMode(permissions: PermissionsDoc | null | undefined): CoverConflictMode {
  const stored = permissions?.houseRules?.coverConflictMode;
  return isCoverConflictMode(stored) ? stored : 'highest';
}

/**
 * How wide a vehicle's front and rear arcs are.
 *
 * DEFAULT 'quarters' - 90° all round, so no table's numbers move without
 * them choosing it. 'narrow' is 60° fore and aft with 120° sides, where a
 * shot 40° off the bow is a flank rather than a frontal.
 *
 * Validated rather than trusted: a later build may write a ruling this
 * one has never heard of, and the document outlives the code.
 */
export function resolveVehicleArcPreset(permissions: PermissionsDoc | null | undefined): ArcPreset {
  const stored = permissions?.houseRules?.vehicleArcPreset;
  return stored && stored in ARC_PRESETS ? stored : 'quarters';
}

/**
 * Does choosing a storey cost the climb?
 *
 * DEFAULT ON. The picker was a free lift: reaching a gantry cost eight
 * metres by drag and nothing by list, and the list was one click. Off,
 * it goes back to being a placement - which is a reasonable ruling for a
 * table that uses it to set scenes rather than to move in a fight.
 *
 * COMING DOWN IS FREE EITHER WAY. That is not this switch's business:
 * it is what stops anyone being stranded on a roof.
 */
export function resolveLevelPickerSpendsMovement(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.levelPickerSpendsMovement !== false;
}

/**
 * Does the Climb skill make it cheaper?
 *
 * DEFAULT ON. Off, everyone pays the untrained rate - for a table that
 * would rather not look up a skill to work out a move.
 */
export function resolveClimbTrainingSetsRate(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.climbTrainingSetsRate !== false;
}

/**
 * Does the combat log get out of the way while somebody is building?
 *
 * DEFAULT ON. Build put an option bar across the top of the map and the
 * log is a curtain hanging over that same column; laying terrain out is
 * not a moment anyone is reading attack narration. Off keeps the log
 * where it is, which is what a GM narrating while they build would want.
 */
export function resolveLogYieldsInBuild(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.logYieldsInBuild !== false;
}

/**
 * Do players get the muster - everyone on the stage, one click to act as them (2026-10-22, plan muster)?
 *
 * DEFAULT OFF, by ruling: the muster is the GM's tool for running the room, and a player with one character has
 * nothing to pick between. Off, a player keeps their squad's chips. The GM always has it.
 */
export function resolvePlayersSeeMuster(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.playersSeeMuster === true;
}

export function resolveReliableNeverJams(permissions: PermissionsDoc | null | undefined): boolean {
  // DEFAULT OFF: the official rule (jam on a confirmed natural 00) is the
  // default; this switch restores the pre-v8.197.17 never-jams behaviour.
  return permissions?.houseRules?.reliableNeverJams === true;
}

export function resolveRoundCountdown(permissions: PermissionsDoc | null | undefined): boolean {
  // DEFAULT ON (unset ⇒ on, explicit false ⇒ off), like the other house
  // rules that make the app do what the rulebook says. A table that has
  // Stunned 3 on a combatant expects it to become Stunned 2; leaving the
  // clock stopped by default meant every timed condition was permanent
  // until somebody found this switch.
  return permissions?.houseRules?.roundCountdown !== false;
}

/**
 * What a condition does to one already worn (v8.367.0).
 *
 * DEFAULT `extend` - what "additive" meant before: the stronger value,
 * clocks that add. The picker and a weapon effect may name their own;
 * this is the answer when nothing does.
 */
export function resolveStackPolicy(permissions: PermissionsDoc | null | undefined): StackPolicy {
  const stored = permissions?.houseRules?.stackPolicy;
  return isStackPolicy(stored) ? stored : DEFAULT_STACK_POLICY;
}

// ── Turn order (v8.71.0) ─────────────────────────────────────

/**
 * Roll initiative for arrivals automatically?
 *
 * DEFAULT ON. Initiative is 1d10 + AB and the app knows both halves; a
 * table that wants to roll its own physical dice turns this off and
 * types the numbers, which still sorts.
 */
export function resolveAutoRollInitiative(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.autoRollInitiative !== false;
}

/**
 * Does someone joining a round already in progress act in it?
 *
 * DEFAULT 'now'. A reinforcement faster than whoever is acting takes
 * their turn next rather than waiting the round out behind slower
 * combatants - which is what a table would rule, and what the
 * first-pending scan gives for nothing.
 */
export function resolveInitiativeArrivals(permissions: PermissionsDoc | null | undefined): InitiativeArrival {
  const stored = permissions?.houseRules?.initiativeArrivals;
  return isInitiativeArrival(stored) ? stored : DEFAULT_INITIATIVE_ARRIVAL;
}

/** What separates two equal initiatives. DEFAULT 'agility', as the core rules have it. */
export function resolveInitiativeTieBreak(permissions: PermissionsDoc | null | undefined): InitiativeTieBreak {
  const stored = permissions?.houseRules?.initiativeTieBreak;
  return isInitiativeTieBreak(stored) ? stored : DEFAULT_INITIATIVE_TIE_BREAK;
}

/**
 * Everyone rerolls at the start of each round?
 *
 * DEFAULT OFF - Deathwatch rolls initiative once and keeps it. On, the
 * order is redrawn every round, which incidentally dissolves the
 * mid-combat-join question entirely.
 */
export function resolveRerollEachRound(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.rerollEachRound === true;
}

/**
 * Does a bulk reroll leave manually typed initiatives alone?
 *
 * DEFAULT OFF: "reroll all" means all, with no exceptions to remember.
 * On, a number somebody typed is a ruling the dice may not overturn -
 * the per-chip reroll is still the way to overturn one deliberately.
 */
export function resolveRerollKeepsManual(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.rerollKeepsManual === true;
}

/**
 * Pass over the turns of the dead and the unconscious?
 *
 * DEFAULT ON. They stay listed at the initiative they already have and
 * are only skipped, so a revival puts them back in the order with
 * nothing to re-enter. Off for a table that plays out a dying
 * character's turn.
 */
export function resolveSkipDownedTurns(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.skipDownedTurns !== false;
}

/**
 * Does somebody recalled to a waiting room keep their place in the order?
 *
 * DEFAULT OFF. A combatant pulled off the board is not there to act, and
 * leaving their chip in the bar means the turn stops on somebody who
 * cannot do anything. On, the room reads as a wing rather than an exit -
 * for a table that shuttles people in and out of one continuous fight.
 */
export function resolveRecallKeepsTurn(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.recallKeepsTurn === true;
}

/**
 * May the map write cover and range onto the target?
 *
 * Defaults to TRUE: deriving those two values from geometry is the whole
 * reason the battlefield exists, and a table that did not want it would
 * not have built a map. Since v7.51.0 the live answer is PROJECTED per
 * attacker rather than written to the target, so turning the rule off
 * mid-session cannot leave a stale number behind - there is nothing
 * stored to go stale. Only a deliberate PIN writes, and it writes manual.
 */
export function resolveBattlefieldDerivedValues(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.battlefieldDerivedValues !== false;
}

/**
 * Does the map apply range and cover WITHOUT being asked?
 *
 * Default TRUE. The geometry already knows the answer and re-derives it
 * the moment anyone moves; making somebody drag a tape and click Apply
 * once per shot was ceremony, and a table that forgot the ceremony
 * resolved a fight against cover that had stopped existing.
 *
 * Off leaves the deliberate `Apply to target` button as the only route,
 * which is exactly how the map behaved before v7.51.0. Needs
 * `battlefieldDerivedValues`, which governs whether the map may speak to
 * the combat engine at all.
 */
export function resolveAutoApplyMapModifiers(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.autoApplyMapModifiers !== false;
}

/**
 * Draw the actor's body map in full detail?
 *
 * Defaults to TRUE, like resolveApplyConditionModifiers and unlike the
 * other two: seeing your own armour, wounds and burning limbs is the
 * behaviour people expect once they have seen it on the target side.
 * Unset ⇒ on; explicit false ⇒ the plain silhouette.
 */
export function resolveDetailedActorBodyMap(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.detailedActorBodyMap !== false;
}

/**
 * May melee and ranged weapons draw from the same pool of special rules?
 *
 * DEFAULT OFF - the core rules keep the two lists apart, and a table that
 * wants a chainsword with Tearing already has one.
 *
 * A RESOLVER LIKE ITS NINETEEN SIBLINGS, since v8.58.0. It was the
 * original house rule and it never grew one: every consumer read
 * `permissions?.houseRules?.…` directly, which is how the default came to
 * be written out by hand at each call site.
 */
export function resolveUnrestrictedWeaponSpecials(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.unrestrictedWeaponSpecials === true;
}

/**
 * How Unnatural Characteristic multipliers affect Degrees of Success.
 *
 * DEFAULT 'off' - the core rules, where a multiplier touches bonuses and
 * soak and leaves tests alone. See engine/unnaturalDos.ts for the four
 * rulings and the math each one applies.
 *
 * Validated rather than trusted, like `resolveCoverConflictMode` and
 * `resolveVehicleArcPreset`: a later build may write a ruling this one
 * has never heard of, and the document outlives the code. It was the odd
 * one out on both counts until v8.58.0 - no resolver, no validator, and
 * the fallback spelled out independently in two components.
 */
/**
 * WHO THE ALARM INTERRUPTS.
 *
 * DEFAULT `actorsAndGm`, because the GM plays almost everything that gets
 * shot at, and a reaction nobody notices expires into damage. A table that
 * finds this noisy in a large fight can narrow it to the players actually
 * holding the character. Neither ruling hides anything: every client lists
 * every owed reaction and anyone may answer one - see `useReactionQueue`.
 */
export function resolveReactionAlarm(permissions: PermissionsDoc | null | undefined): ReactionAlarmPolicy {
  const stored = permissions?.houseRules?.reactionAlarm;
  return isReactionAlarmPolicy(stored) ? stored : DEFAULT_REACTION_ALARM;
}

/**
 * Does a missed blast go somewhere?
 *
 * DEFAULT ON. A grenade that misses and simply ceases to exist is the least
 * interesting outcome available, and scatter is where friendly fire earns
 * its place. Off makes a miss a miss, for tables that would rather not track
 * the template.
 */
export function resolveBlastScatter(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.blastScatter !== false;
}

/**
 * Is dread tested on EVERY attack, rather than once per target? (v8.354.0)
 *
 * DEFAULT OFF - the ruling: an attacker faces a Terrifying thing once,
 * and the answer is remembered on their state. On, for tables that want
 * the horror felt every swing; nothing is remembered then.
 */
export function resolveDreadPerAttack(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.dreadPerAttack === true;
}

/**
 * Can a blast catch the one who threw it? See `engine/blastRules.ts`.
 *
 * DEFAULT `onScatter` - intent decides, and a melee blast never scatters.
 */
export function resolveBlastCatchesAttacker(permissions: PermissionsDoc | null | undefined): BlastSelfPolicy {
  const stored = permissions?.houseRules?.blastCatchesAttacker;
  return isBlastSelfPolicy(stored) ? stored : DEFAULT_BLAST_SELF;
}

export function resolveUnnaturalDosMode(permissions: PermissionsDoc | null | undefined): UnnaturalDosMode {
  const stored = permissions?.houseRules?.unnaturalDosMode;
  return isUnnaturalDosMode(stored) ? stored : DEFAULT_UNNATURAL_DOS_MODE;
}

/**
 * Does terrain adjudicate anything, or is the map a diagram?
 *
 * DEFAULT ON (terrain counts). Off, cover, line of sight, movement and
 * the climb all stop consulting the map, for a table that wants the
 * picture without the geometry deciding their fights.
 *
 * A SESSION RULING WEARING A VIEW OPTION'S CLOTHES until v8.63.0. It sat
 * in the row of private display preferences on the map's toolbar,
 * styled exactly like them, with no GM gate - and its own tooltip said
 * "for everyone", which was the giveaway. Any player could switch the
 * geometry off for the whole table.
 *
 * `stored` is the legacy home, `terrain.ignoreTerrain` on the map
 * document. It still wins where the house rule has never been written,
 * so no existing session's map changes underneath it.
 */
export function resolveIgnoreTerrain(
  permissions: PermissionsDoc | null | undefined,
  stored?: boolean | undefined,
): boolean {
  const rule = permissions?.houseRules?.ignoreTerrain;
  return rule === undefined ? stored === true : rule === true;
}

/**
 * Whether the Protagonist rules apply at all, for anyone.
 *
 * DEFAULT ON since v8.185.0. It shipped off, on the argument that
 * characters travel and being handed one should not quietly ship plot
 * armour into your game - but off-by-default has a worse failure mode in
 * practice: a GM fills in the tab, plays, and watches the character die
 * anyway, with nothing on screen connecting the two. That happened
 * within a day.
 *
 * A character still has to carry `enabled` of its own, so a sheet that
 * travels with its rules (they are part of it, 2026-11-04) changes
 * nothing until somebody switched them on for it. A table that wants none of this turns one
 * switch off; a GM who forgets it loses a character.
 *
 * Unset means ON, so a session that predates the feature gets it - and
 * that costs nothing, because no character in it carries `enabled`.
 */
export function resolveProtagonistRules(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.protagonistRules !== false;
}

/**
 * Whether wounds are shown as a band rather than a number.
 *
 * DEFAULT OFF. Also redacts the damage numbers in the log, because a
 * band beside a log that prints "7 damage" is decoration - the players
 * simply keep a tally.
 */
export function resolveBandedWounds(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.bandedWounds === true;
}

/** Whether damage rolls take their average, and for whom. Validates rather than trusts. */
export function resolveAveragedDamage(permissions: PermissionsDoc | null | undefined): AveragedDamageMode {
  const stored = permissions?.houseRules?.averagedDamage;
  return isAveragedDamageMode(stored) ? stored : DEFAULT_AVERAGED_DAMAGE;
}

/**
 * The table's default for a viewer setting (`tableDefault` in the registry):
 * what a screen whose viewer has chosen nothing comes to (ADR 2026-10-07).
 */
export function resolveTableDefault(
  permissions: PermissionsDoc | null | undefined,
  viewerKey: string,
): boolean | string {
  const def = settingByKey(viewerKey);
  if (!def?.tableDefault) throw new Error(`resolveTableDefault: ${viewerKey} is not a setting the table may default`);
  return viewerChoice(def, undefined, tableDefaultsFrom(permissions?.houseRules));
}

/** What a failed Cohesion Challenge does (Squad Mode, 2026-10-23). Validates rather than trusts; default 'nothing'. */
export function resolveCohesionChallengeFailure(permissions: PermissionsDoc | null | undefined): ChallengeFailure {
  const stored = permissions?.houseRules?.cohesionChallengeFailure;
  return isChallengeFailure(stored) ? stored : DEFAULT_CHALLENGE_FAILURE;
}

/** Whether heavy hits cost a kill-team Cohesion. Unset means ON. */
export function resolveCohesionDamage(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.cohesionDamage !== false;
}

/** Whether a brother has one squad ability's benefit between his turns. Unset means ON. */
export function resolveOneSquadAbilityPerTurn(permissions: PermissionsDoc | null | undefined): boolean {
  return permissions?.houseRules?.oneSquadAbilityPerTurn !== false;
}
